"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const moveSizeOptions = ["Solo Artist / Tracking", "3-Piece Band / Rehearsal", "Full Band / Live Session", "Large Ensemble / Production", "Commercial / Podcast"];
function DispatchForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="relative p-10 border border-[var(--border)] bg-[var(--card)]">
        <span className="absolute top-2 left-2 text-[var(--chart-1)] font-mono text-xs">+</span>
        <span className="absolute top-2 right-2 text-[var(--chart-1)] font-mono text-xs">+</span>
        <span className="absolute bottom-2 left-2 text-[var(--chart-1)] font-mono text-xs">+</span>
        <span className="absolute bottom-2 right-2 text-[var(--chart-1)] font-mono text-xs">+</span>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--chart-1)] mb-4">
          § STATUS / SIGNAL_LOCKED
        </p>
        <h3 className="font-default text-3xl font-bold text-foreground mb-4">
          Session <em className="text-[var(--chart-1)] not-italic" style={{
          fontStyle: "italic"
        }}>confirmed</em>.
        </h3>
        <p className="text-foreground/85 mb-6 leading-relaxed font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-[var(--border)] bg-transparent text-foreground hover:bg-[var(--chart-1)] hover:text-[var(--charcoal-deep)] hover:border-[var(--chart-1)] font-mono text-xs uppercase tracking-[0.16em] rounded-md">
          Book Another Slot
        </Button>
      </div>;
  }
  return <div className="relative bg-[var(--card)] border border-[var(--border)] p-6 md:p-10">
      {}
      <span className="absolute top-2 left-2 text-[var(--chart-1)] font-mono text-sm leading-none select-none">+</span>
      <span className="absolute top-2 right-2 text-[var(--chart-1)] font-mono text-sm leading-none select-none">+</span>
      <span className="absolute bottom-2 left-2 text-[var(--chart-1)] font-mono text-sm leading-none select-none">+</span>
      <span className="absolute bottom-2 right-2 text-[var(--chart-1)] font-mono text-sm leading-none select-none">+</span>

      <div className="mb-8 flex items-center gap-3">
        <span className="block h-px w-12 bg-[var(--chart-1)]"></span>
        <span className="block h-1 w-1 bg-[var(--chart-1)]"></span>
        <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-[var(--chart-1)]">
          § SESSION_INQUIRY — INPUT_GAIN
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="full_name" rules={{
          required: "Full name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Artist / Band Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="The Sound Waves" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Contact Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="booking@band.com" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[+\d\s().-]{7,}$/,
            message: "Invalid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(555) 000-0000" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="move_date" rules={{
          required: "Session date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Requested Session Date
                </FormLabel>
                <FormControl>
                  <Input type="date" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] [color-scheme:dark] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="origin_address" rules={{
          required: "Project type is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Project Type
                </FormLabel>
                <FormControl>
                  <Input placeholder="EP Tracking / Live Rehearsal" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="destination_address" rules={{
          required: "Duration is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Estimated Duration
                </FormLabel>
                <FormControl>
                  <Input placeholder="4 Hours / Full Day" {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] rounded-md" />
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />

          <FormField control={control} name="move_size" rules={{
          required: "Please select session size"
        }} render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                  Session Scale
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 h-auto w-full text-foreground focus-visible:ring-0 focus-visible:border-[var(--chart-1)] data-[placeholder]:text-foreground/30 font-mono text-xs uppercase tracking-[0.1em] rounded-md">
                      <SelectValue placeholder="SELECT SCALE" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--card)] border border-[var(--border)] text-foreground rounded-md">
                      {moveSizeOptions.map((option, i) => <SelectItem key={option} data-index={i} value={option} className="font-mono text-xs uppercase tracking-[0.1em] focus:bg-[var(--chart-1)] focus:text-[var(--charcoal-deep)]">
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
              </FormItem>} />
        </div>

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/60">
                Backline & Gear Requirements
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tube amps, specific drum kits, or PA needs..." rows={5} {...field} className="bg-[var(--background)] border-0 border-b-2 border-[var(--chart-5)] px-3 py-3 text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-[var(--chart-1)] resize-none rounded-md" />
              </FormControl>
              <FormMessage className="font-mono text-xs text-[var(--destructive)]" />
            </FormItem>} />

        {submitError && <div className="p-4 border border-[var(--destructive)] bg-[var(--destructive)]/10">
            <p className="text-[var(--destructive)] font-mono text-xs uppercase tracking-[0.12em]">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-[var(--chart-1)] text-[var(--charcoal-deep)] hover:bg-[var(--chart-1)] hover:translate-x-1 hover:translate-y-1 shadow-md font-default font-bold text-xs uppercase tracking-[0.16em] py-6 h-auto group rounded-md">
          {isSubmitting ? <span>Syncing...</span> : <>
              <span>Lock In Session</span>
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>

        <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/50 text-center pt-2">
          Independent Space. Community Driven.
        </p>
      </Form>
    </div>;
}
export default function SessionInquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1666" id="session-inquiry" className="bg-background text-foreground py-24 md:py-44 px-6 md:px-22 relative overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {}
        <div ref={leftRef} className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start relative transition-all duration-700" style={{
        opacity: leftVisible ? 1 : 0,
        transform: leftVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          {}
          <div aria-hidden="true" className="absolute -top-16 -left-4 font-default font-extrabold text-foreground/[0.03] select-none pointer-events-none leading-none" style={{
          fontSize: "28vw"
        }}>
            01
          </div>

          <div className="relative">
            <div className="mb-8 flex items-center gap-3">
              <span className="block h-px w-20 bg-[var(--chart-1)]"></span>
              <span className="block h-1 w-1 bg-[var(--chart-1)]"></span>
            </div>
            <p className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-[var(--chart-1)] mb-8">
              § 01 / BOOKING — SESSION_INQUIRY
            </p>

            <h1 className="font-default font-extrabold text-foreground leading-[0.9] tracking-[-0.04em] mb-8 uppercase" style={{
            fontSize: "clamp(3.5rem, 8vw, 7rem)"
          }}>
              Book The{" "}
              <em className="text-[var(--chart-1)]" style={{
              fontStyle: "italic",
              fontFamily: "var(--font-serif)"
            }}>
                Room
              </em>
            </h1>

            <p className="text-foreground/85 leading-relaxed mb-12 max-w-md font-default" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)"
          }}>
              Ready to track? Submit your session details and gear requirements. Our engineers will confirm your slot within four hours.
            </p>

            {}
            <div className="space-y-5 border-t border-[var(--border)] pt-8">
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/50 min-w-[110px]">
                  Studio_Line
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.1em] text-foreground">
                  (555) 808-4444
                </span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/50 min-w-[110px]">
                  Email_Link
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.1em] text-foreground break-all">
                  noise@thegarageroom.com
                </span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/50 min-w-[110px]">
                  Availability
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.1em] text-foreground">24 / 7 ACCESS</span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-foreground/50 min-w-[110px]">
                  Signal_Code
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.1em] text-[var(--chart-1)]">GRG → LIVE</span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className="lg:col-span-7 transition-all duration-700 delay-100" style={{
        opacity: rightVisible ? 1 : 0,
        transform: rightVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <FormProvider formKey="session_inquiry_form_book" formId="session_inquiry_form_book" sectionName="initiate-your-move" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          move_date: "",
          origin_address: "",
          destination_address: "",
          move_size: "",
          additional_details: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          move_date: {
            required: true
          },
          origin_address: {
            required: true
          },
          destination_address: {
            required: true
          },
          move_size: {
            required: true
          }
        }} submitText="Lock In Session" successMessage="Signal received. A studio engineer will confirm your booking within four hours." errorMessage="Transmission failed. Check your signal and retry or call the studio line.">
            <DispatchForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}